import { useEffect, useRef, useState } from "react";
import { useCookies } from "react-cookie";
import { toast } from "react-toastify";
import { Modal } from "react-bootstrap";
import http from "../../../common/http-common";
import * as common from "../../../common/common";
import * as Comp from "../../../common/CommonComponents";
import save from "../../../assets/save-icon.png";
import cancel from "../../../assets/cancel-icon.png";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";

const ModifySystem = (props) => {

	const [cookies] = useCookies([]);
	const [userId] = useState(cookies.USER_ID);

	const systemInputRef = useRef();
	const remarkInputRef = useRef();

	const [editData, setEditData] = useState([]);
	const [inputError, setInputError] = useState([]);

	// ATTACHMENT
	const [selectedFiles, setSelectedFiles] = useState([]);
	const [existingAttachment, setExistingAttachment] = useState(null);
	const [isRemoved, setIsRemoved] = useState(false);
	const [columns, setColumns] = useState([]);

	const fileData = [...(
		[
			...(existingAttachment && !isRemoved ? [existingAttachment] : []),
			...selectedFiles
		]
	)];

	//console.log("selectedFiles:", selectedFiles);
	//console.log("fileData:", fileData);

	// ================= INIT =================
	useEffect(() => {
		if (props.editData !== null) {
			setEditData({
				SYSTEM_MST_ID: props.editData.SYSTEM_MST_ID,
				SYSTEM_MST_NAME: props.editData.SYSTEM_MST_NAME,
				REMARK: props.editData.REMARK ?? "",
			});

			if (props.editData.ATTACHMENT_NAME && props.editData.ATTACHMENT_NAME !== "-") {
				setExistingAttachment({
					name: props.editData.ATTACHMENT_NAME,
					size: 0
				});
				setIsRemoved(false);
			} else {
				setExistingAttachment(null);
				setIsRemoved(false);
			}
		}
	}, [props.editData]);

	// ================= MODAL =================
	const hideModal = () => {
		setInputError([]);
		setSelectedFiles([]);
		setExistingAttachment(null);
		setIsRemoved(false);
		props.onHide();
	};

	// ================= SAVE =================
	const saveBtnOnClick = () => {
		if (Object.values(inputError).filter(v => v !== "").length === 0) {
			SaveEditSystem();
		}
	};

	function SaveEditSystem() {
		let functionName = SaveEditSystem.name;

		try {
			props.onLoading(true, "Modifying system...");

			//WITH FILE or REMOVE EXISTING
			if (selectedFiles.length > 0 || isRemoved) {

				const formData = new FormData();

				//REQUIRED DATA
				formData.append("SYSTEM_MST_ID", editData.SYSTEM_MST_ID);
				formData.append("SYSTEM_MST_NAME", editData.SYSTEM_MST_NAME?.trim() || "");
				formData.append("REMARK", editData.REMARK?.trim() || "");
				formData.append("UPDATE_ID", userId);
				formData.append("REMOVE_EXISTING", isRemoved ? "Y" : "N");

				//FILE
				selectedFiles.forEach((item) => {
					formData.append("file", item.file);
				});

				http.post(
					common.c_getApiUrl() + "api/SystemSetup/UpdateSysWithAttachment",
					formData
				)
				.then(() => {
					toast.success("System updated.");
					props.onReload();
					hideModal();
				})
				.catch((err) => {
					toast.error("Failed to modify System.");
					common.c_LogWebError(props.page, functionName, err);
				})
				.finally(() => props.onLoading(false));
			}

			//NO FILE, NORMAL UPDATE
			else {
				const data = {
					SYSTEM_MST_ID: editData.SYSTEM_MST_ID,
					SYSTEM_MST_NAME: editData.SYSTEM_MST_NAME?.trim() || "",
					REMARK: editData.REMARK?.trim() || "",
					UPDATE_ID: userId,
					FROM_SOURCE: { SOURCE: "WEB", MODULE_ID: props.module },
				};

				http.put("api/SystemSetup/UpdateSys", data)
					.then(() => {
						toast.success("System updated.");
						props.onReload();
						hideModal();
					})
					.catch((err) => {
						toast.error("Failed to modify System.");
						common.c_LogWebError(props.page, functionName, err);
					})
					.finally(() => props.onLoading(false));
			}

		} catch (err) {
			props.onLoading(false);
			toast.error("Failed to modify System.");
		}
	}

	// ================= INPUT =================
	const inputRequiredHandler = (event) => {
		const id = event.target.id;
		const val = event.target.value;

		setInputError((prev) => ({
			...prev,
			[id]: val.trim() ? "" : "Required"
		}));

		setEditData((prev) => ({
			...prev,
			[id]: val,
		}));
	};

	const inputUnrequiredHandler = (event) => {
		const id = event.target.id;
		const val = event.target.value;

		setEditData((prev) => ({
			...prev,
			[id]: val,
		}));
	};

	// ================= FILE =================
	const selectFileHandler = (event) => {
		const file = event.target.files[0];

		if (!file) return;

		if (existingAttachment && !isRemoved) {
			toast.error("Remove existing file first.");
			return;
		}

		if (file.size > 4 * 1024 * 1024) {
			toast.error("File exceeds 4MB.");
			return;
		}

		setSelectedFiles([{
			name: file.name,
			size: file.size,
			file: file // 🔥 keep original file for upload
		}]);
		event.target.value = null;
	};

	const removeExistingFile = () => {
		setIsRemoved(true);
		setExistingAttachment(null);
	};

	const clearFiles = (e, row) => {
		setSelectedFiles([]);
	};

	const formatSize = (bytes) => {
		if (!bytes || bytes === 0) return "-";
		const sizes = ["Bytes", "KB", "MB"];
		const i = Math.floor(Math.log(bytes) / Math.log(1024));
		return Math.round(bytes / Math.pow(1024, i)) + " " + sizes[i];
	};

	function chooseFileFn() {
		document.getElementById("ATTACHMENT").click();
	}

	// ================= TABLE =================
	useEffect(() => {
		setColumns([
			{
				accessorFn: (row) => row.name,
				header: "Filename",
			},
			{
				accessorFn: (row) => row.size === " " ? " " : formatSize(row.size),
				header: "Size",
			},
			{
				header: "Action",
				accessorFn: (row) => (
					<button
						style={{ background: "none", border: "none" }}
						onClick={(e) => {
							if (existingAttachment && row.name === existingAttachment.name) {
								removeExistingFile();
							} else {
								clearFiles(e, row);
							}
						}}
					>
						<CloseRoundedIcon />
					</button>
				),
			}
		]);
	}, [selectedFiles, existingAttachment, isRemoved]);

	// ================= UI =================
	return (
		<Modal show={props.showHide} onHide={hideModal} centered>
			<Modal.Header>
				<div style={{ backgroundColor: "lightgrey", width: "100%" }}>
					<Modal.Title>MODIFY</Modal.Title>
				</div>
			</Modal.Header>

			<Modal.Body>

				<Comp.Input
					ref={systemInputRef}
					id="SYSTEM_MST_NAME"
					label="System Name*:"
					value={editData.SYSTEM_MST_NAME}
					errorMessage={inputError.SYSTEM_MST_NAME}
					onChange={inputRequiredHandler}
				/>

				<Comp.Input
					ref={remarkInputRef}
					id="REMARK"
					label="Remark:"
					value={editData.REMARK}
					onChange={inputUnrequiredHandler}
				/>

				<input
					id="ATTACHMENT"
					type="file"
					style={{ display: "none" }}
					onChange={selectFileHandler}
					accept=".doc,.docx,.xls,.xlsx,.pdf,.jpg,.jpeg,.png,.txt"
				/>

				<div style={{ marginTop: "15px" }}>
					<div style={{ marginBottom: "8px", fontWeight: "500" }}>
						Upload File (Max. 4MB)
					</div>

					{/* Upload button */}
					{fileData.length === 0 && (
						<Comp.Button onClick={chooseFileFn}>
							Upload File
						</Comp.Button>
					)}

					{/* Existing label */}
					{/* {existingAttachment && !isRemoved && (
						<div style={{ marginTop: "10px", fontSize: "14px" }}>
							<span style={{ color: "#555" }}>Existing file:</span>{" "}
							<span style={{ color: "#007bff" }}>{existingAttachment.name}</span>
						</div>
					)} */}

					{/* Table */}
					{fileData.length > 0 && (
						<div style={{ marginTop: "10px" }}>
							<Comp.Table data={fileData} columns={columns} enableTopToolbar={false} />
						</div>
					)}

					{/* Clear button */}
					{selectedFiles.length > 0 && (
						<Comp.Button
							type="delete"
							style={{ float: "right", marginTop: "5px" }}
							onClick={() => setSelectedFiles([])}
						>
							Clear
						</Comp.Button>
					)}

				</div>
			</Modal.Body>

			<Modal.Footer>
				<div style={{ textAlign: "center", width: "100%" }}>
					<Comp.Button onClick={saveBtnOnClick}>
						Save <img src={save} alt="" style={{ width: 16 }} />
					</Comp.Button>

					<Comp.Button type="cancel" onClick={hideModal}>
						Cancel <img src={cancel} alt="" style={{ width: 16 }} />
					</Comp.Button>
				</div>
			</Modal.Footer>
		</Modal>
	);
};

export default ModifySystem;